import { VOUCHER_TAG_REQUESTED_CHANGE } from '../entities/traveler/voucher_liveness.js';

/**
 * Portão determinístico de cancelamento de voucher — a MESMA lógica do agente
 * (`apps/services/agents/src/domains/voucher/logic/gating.ts`), elevada para
 * packages/domain sobre um input estrutural para que o BFF e o agente
 * compartilhem UMA definição. Precedência: tag bloqueante → em emissão →
 * cancelado → emitido → pré-emissão → não resolvido.
 */
type VoucherCancelDecision = 'cancel_pre' | 'request_post' | 'blocked' | 'unresolved';
type VoucherBlockReason = 'in_emission' | 'already_canceled' | 'blocking_tag';

export interface VoucherCancellationInput {
  statusCode: string | null;
  statusName: string | null;
  tags: string[];
}

export interface VoucherCancellationVerdict {
  decision: VoucherCancelDecision;
  blockReason?: VoucherBlockReason;
  postEmission: boolean;
}

const PRE_EMISSION = new Set(['awaiting_approval', 'requested']);
const EMITTED = new Set(['emitted', 'emitido']);
const IN_EMISSION = new Set(['waiting_for_emission', 'aguardando_emissao']);
const CANCELED = new Set(['canceled', 'cancelled', 'cancelado']);
const BLOCKING_TAGS = new Set(['not_use', 'not_used', VOUCHER_TAG_REQUESTED_CHANGE.toLowerCase()]);

const norm = (v: string | null | undefined): string => (v ?? '').trim().toLowerCase();

export function evaluateVoucherCancellation(
  input: VoucherCancellationInput,
): VoucherCancellationVerdict {
  const tags = input.tags.map(norm);
  if (tags.some((t) => BLOCKING_TAGS.has(t))) {
    return { decision: 'blocked', blockReason: 'blocking_tag', postEmission: false };
  }
  const candidates = [norm(input.statusCode), norm(input.statusName)].filter(Boolean);
  const matches = (set: Set<string>) => candidates.some((c) => set.has(c));
  if (matches(IN_EMISSION)) {
    return { decision: 'blocked', blockReason: 'in_emission', postEmission: false };
  }
  if (matches(CANCELED)) {
    return { decision: 'blocked', blockReason: 'already_canceled', postEmission: false };
  }
  if (matches(EMITTED)) return { decision: 'request_post', postEmission: true };
  if (matches(PRE_EMISSION)) return { decision: 'cancel_pre', postEmission: false };
  return { decision: 'unresolved', postEmission: false };
}
